import { useMemo } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";

import Button from "../../components/Button";
import Loader from "../../components/Loader";
import { getMe } from "../../services/user.service";
import {
  getRevenueCatCustomerInfo,
  getRevenueCatWebConfig,
  purchaseYearly,
} from "../../services/revenuecat.service";
import { Body, Container, Header } from "./EditProfile";
import BackButton from "../../components/buttons/BackButton";
import Link from "../../components/Link";

const Card = styled.div`
  background: var(--white);
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  padding: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ArpagonPro = () => {
  const { entitlementId, yearlyProductId, discountedYearlyProductId } =
    getRevenueCatWebConfig();
  const { data: user, isLoading: isLoadingUser } = useQuery(["user"], getMe);

  const {
    data: customerInfo,
    isLoading: isLoadingCustomerInfo,
    refetch: refetchCustomerInfo,
  } = useQuery(
    ["revenuecatCustomerInfo", user?.id],
    () => getRevenueCatCustomerInfo(user!),
    { enabled: !!user }
  );

  const isPro = useMemo(() => {
    if (!customerInfo) return false;
    const entitlement = customerInfo.entitlements.active?.[entitlementId];
    return !!entitlement?.isActive;
  }, [customerInfo, entitlementId]);

  const expirationDate = useMemo(() => {
    if (!customerInfo) return null;
    const entitlement = customerInfo.entitlements.active?.[entitlementId];
    return entitlement?.expirationDate;
  }, [customerInfo, entitlementId]);

  const trialDaysRemaining = useMemo(() => {
    if (!user) return null;
    if (user.isRestricted) return null;
    if (isPro) return null;
    if (!user.firstLoggedAtSincePaywall) return null;

    const start = new Date(user.firstLoggedAtSincePaywall);
    if (Number.isNaN(start.getTime())) return null;

    const elapsedDays = Math.floor(
      (Date.now() - start.getTime()) / (24 * 60 * 60 * 1000)
    );
    const remaining = 14 - elapsedDays;
    return remaining > 0 ? remaining : null;
  }, [user, isPro]);

  if (isLoadingUser) return <Loader />;
  if (!user) return <div>Veuillez vous connecter.</div>;

  const managementURL = customerInfo?.managementURL;
  const referralCode = user.referralCode || "—";
  const referralCredits = user.referralCredits || 0;
  const eligibleForDiscount = referralCredits > 0;

  const referredCount = user.referredUsersCount || 0;
  const convertedCount = user.referralConvertedCount || 0;

  return (
    <Body>
      <Header>
        <div className="left mobile-only">
          <Link to="/profil">
            <BackButton />
          </Link>
        </div>
        <div className="title" style={{ margin: "10px 0" }}>
          Abonnement
        </div>
        <div className="right"></div>
      </Header>
      <Container>
        <Row style={{ marginTop: "16px" }}>
          <Card>
            <div style={{ marginBottom: 8 }}>
              L'abonnement coûte 20€ par an, et débloque l'accès à la page
              Patrimoine.
            </div>
            <div>Chaque utilisateur bénéficie de 2 semaines d'essai gratuit.</div>
            {trialDaysRemaining !== null && (
              <div style={{ marginTop: 8, fontWeight: 600 }}>
                Il vous reste {trialDaysRemaining} jour
                {trialDaysRemaining > 1 ? "s" : ""} d'essai.
              </div>
            )}
          </Card>

          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Abonnement</div>
            <div>
              Statut:{" "}
              {isLoadingCustomerInfo ? (
                "…"
              ) : isPro ? (
                <>
                  Actif
                  {expirationDate &&
                    ` jusqu'au ${new Date(expirationDate).toLocaleDateString(
                      "fr-FR",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    )}`}
                </>
              ) : (
                "Inactif"
              )}
            </div>
          </Card>

          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Parrainage</div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Votre code :</span> {referralCode}
            </div>
            <div style={{ marginBottom: 8 }}>
              Si un utilisateur s'abonne après avoir utilisé ce code, vous aurez
              une réduction de 50% sur le prix de votre prochain abonnement.
            </div>
            <div>
              {referredCount === 0 ? (
                "Aucun utilisateur n'a créé de compte avec votre code."
              ) : (
                <>
                  {referredCount} utilisateur{referredCount > 1 ? "s" : ""}{" "}
                  {referredCount > 1 ? "ont" : "a"} créé un compte avec votre code
                  et{" "}
                  {convertedCount === 0
                    ? "aucun n'a"
                    : `${convertedCount} ${
                        convertedCount > 1 ? "ont" : "a"
                      }`}{" "}
                  pris un abonnement.
                </>
              )}
            </div>
          </Card>

          {!isPro && (
            <Button
              size="fullWidth"
              loading={isLoadingCustomerInfo}
              onClick={async () => {
                const productId = eligibleForDiscount
                  ? discountedYearlyProductId
                  : yearlyProductId;
                await purchaseYearly(user, { productId });
                await refetchCustomerInfo();
              }}
            >
              {eligibleForDiscount ? "S'abonner (Annuel -50%)" : "S'abonner (Annuel)"}
            </Button>
          )}

          {/* <Button
            size="fullWidth"
            variant="white"
            loading={isLoadingCustomerInfo}
            onClick={() => refetchCustomerInfo()}
          >
            Rafraîchir mon statut (restauration)
          </Button> */}

          {managementURL && (
            <Button
              size="fullWidth"
              variant="white"
              onClick={() =>
                window.open(managementURL, "_blank", "noopener,noreferrer")
              }
            >
              Gérer mon abonnement
            </Button>
          )}
        </Row>
      </Container>
    </Body>
  );
};

export default ArpagonPro;

